import type { GameEvent } from "../../engine/types";

export const REPTILE_EVENTS: readonly GameEvent[] = [
  {
    id: "r_alone",
    opening: true,
    archetypes: ["REPTILE"],
    stages: ["hatchling"],
    once: true,
    weight: 10,
    text: "ไม่มีพ่อแม่รออยู่ คุณต้องเอาตัวรอดเองตั้งแต่วินาทีแรก",
    fact: "สัตว์เลื้อยคลานส่วนใหญ่ไม่ดูแลลูก ลูกที่ฟักออกมาต้องหาอาหารและหลบภัยเองทันที แต่จระเข้เป็นข้อยกเว้น",
    choices: [
      { label: "รีบหาที่ซ่อน", outcomes: [{ chance: 1, text: "คุณมุดเข้าใต้กองใบไม้แห้งได้ทัน", effects: { safety: 10 } }] },
      {
        label: "ออกหาอาหารมื้อแรก",
        outcomes: [
          { chance: 0.7, text: "คุณจับแมลงตัวเล็กๆ ได้เป็นมื้อแรก", effects: { fullness: 20 } },
          { chance: 0.3, text: "นกเหลือบมาเห็นคุณ", effects: { safety: -15 } },
        ],
      },
    ],
  },
  {
    id: "r_buried_egg",
    archetypes: ["REPTILE"],
    stages: ["egg"],
    weight: 6,
    text: "คุณอยู่ใต้ดินอุ่นๆ ที่แม่ขุดฝังไว้ แม่จากไปนานแล้ว",
    choices: [{ label: "รอ", outcomes: [{ chance: 1, text: "ความอุ่นของดินช่วยให้คุณเติบโต", effects: { health: 5 } }] }],
  },
  {
    id: "r_turtle_beach",
    opening: true,
    archetypes: ["REPTILE"],
    stages: ["hatchling"],
    requires: { subgroups: ["reptile.turtle"], habitat: ["sea"] },
    once: true,
    weight: 30,
    text: "คุณกับพี่น้องฟักออกมาบนชายหาดตอนกลางคืน ต้องไปให้ถึงทะเล",
    choices: [
      {
        label: "คลานไปทางแสงจันทร์บนผิวน้ำ",
        outcomes: [
          { chance: 0.7, text: "คลื่นลูกแรกพัดคุณลงสู่ทะเล", effects: { safety: 10 } },
          { chance: 0.3, text: "ปูลมกับนกทะเลรออยู่บนหาด", effects: { death: "predator" } },
        ],
      },
      {
        label: "คลานไปทางแสงไฟสว่างๆ",
        outcomes: [
          { chance: 0.3, text: "คุณรู้ตัวว่าผิดทางและกลับตัวทัน", effects: { fullness: -15 } },
          { chance: 0.7, text: "แสงไฟจากเมืองหลอกให้คุณคลานห่างจากทะเลไปเรื่อยๆ", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "r_bask",
    archetypes: ["REPTILE"],
    stageTags: ["young", "adult", "old"],
    weight: 7,
    text: "แดดอุ่นๆ ส่องลงมา ร่างกายของคุณเย็นเฉียบ",
    choices: [
      {
        label: "นอนอาบแดด",
        outcomes: [
          { chance: 0.8, text: "ร่างกายอุ่นขึ้น คุณกลับมาว่องไวอีกครั้ง", effects: { health: 10 } },
          { chance: 0.2, text: "คุณอาบแดดในที่โล่งเกินไป นกล่าเหยื่อเริ่มบินวน", effects: { health: 10, safety: -15 } },
        ],
      },
      { label: "หลบในร่ม", outcomes: [{ chance: 1, text: "ปลอดภัย แต่ร่างกายเย็นและเชื่องช้าไปทั้งวัน", effects: { safety: 5, fullness: -5 } }] },
    ],
  },
  {
    id: "r_ambush",
    archetypes: ["REPTILE"],
    stageTags: ["young", "adult", "old"],
    requires: { diet: ["carnivore", "insectivore"] },
    weight: 7,
    text: "คุณซุ่มนิ่งอยู่ เหยื่อตัวหนึ่งกำลังเดินเข้ามาใกล้",
    choices: [
      {
        label: "รอให้ใกล้อีกนิด",
        outcomes: [
          { chance: 0.65, text: "จู่โจมครั้งเดียวสำเร็จ อาหารมื้อนี้อยู่ได้อีกหลายวัน", effects: { fullness: 45 } },
          { chance: 0.35, text: "มันเปลี่ยนทางไปเสียก่อน", effects: { fullness: -5 } },
        ],
      },
      {
        label: "จู่โจมเดี๋ยวนี้",
        outcomes: [
          { chance: 0.45, text: "ทันพอดี!", effects: { fullness: 40 } },
          { chance: 0.55, text: "เร็วเกินไป มันหนีไปได้", effects: { fullness: -5 } },
        ],
      },
    ],
  },
  {
    id: "r_graze",
    archetypes: ["REPTILE"],
    stageTags: ["young", "adult", "old"],
    requires: { diet: ["herbivore", "omnivore"] },
    weight: 7,
    text: "คุณเจอพืชอ่อนๆ ที่ชอบกิน",
    choices: [
      { label: "กินช้าๆ ให้อิ่ม", outcomes: [{ chance: 1, text: "อิ่มสบายท้อง", effects: { fullness: 30 } }] },
      {
        label: "กินแล้วรีบไปหาที่ซ่อน",
        outcomes: [{ chance: 1, text: "คุณกินไปได้ครึ่งเดียวแต่ปลอดภัย", effects: { fullness: 15, safety: 5 } }],
      },
    ],
  },
  {
    id: "r_shed",
    archetypes: ["REPTILE"],
    stageTags: ["young", "adult"],
    requires: { subgroups: ["reptile.snake", "reptile.lizard"] },
    weight: 3,
    text: "ผิวหนังเก่าเริ่มตึงและหมอง ถึงเวลาลอกคราบ",
    choices: [
      { label: "หาที่ซ่อนแล้วค่อยลอก", outcomes: [{ chance: 1, text: "คุณได้ผิวใหม่ที่สดใส", effects: { health: 10, safety: 5 } }] },
      { label: "ลอกระหว่างทางเลย", outcomes: [{ chance: 1, text: "ระหว่างลอกคราบ ตาของคุณพร่ามัวจนเกือบเดินชนนักล่า", effects: { health: 10, safety: -15 } }] },
    ],
  },
];
